// Ported from Dealtracker src/appframe/tours/TourRunner.tsx (commit 30ca0cb) — port, don't share
// (DEBT.md, "tours engine duplicated from Dealtracker"). The run logic (start / advance / back /
// furthest step / probe edge / resume / congratulations) is Dealtracker's; the chrome is rewritten
// on Niobe's tokens (`.tour-*` in styles.css) with plain portals, the router is lib/router.ts and
// the strings are i18n.tsx's `tour_*` keys.
import {
  createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState,
  useSyncExternalStore, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import type { View } from '../App'
import { useLang, type Lang } from '../i18n'
import {
  accountSettingsPath, bidPath, corporateTemplatesPath, dashboardPath, feedbackTriagePath, homePath, knowledgePath,
  navigate, parseRoute, releasesPath, settingsPath, usePath, versionRetentionPath,
} from '../lib/router'
import { TOUR_DEFS } from './defs'
import { helpIdSelector } from './helpIds'
import { evaluateCondition, evaluateEmpty, makeProbeContext } from './probes'
import { subscribeTourState } from './useTourState'
import { clearActiveTour, readActiveTour, writeActiveTour, type ActiveTour } from './session'
import { TourMarkdown, tourMarkdownToPlain } from './markdown'
import { HighlightRing, RING_WIDTH, useAnchorRect } from './HighlightRing'
import { clampToViewport, computePlacement, nudgeOut, type Point } from './placement'
import { TOUR_WINDOW, useAvoidSet } from './avoidSet'
import { RatingProvider, useOpenRating } from './rating'
import { Confetti } from './Confetti'
import type { TourDef, TourStep } from './types'

/** A step whose anchor is still missing after this long offers Skip step / Stop tour. */
export const ANCHOR_TIMEOUT_MS = 5000
/** The step window's width; it docks full-width at the bottom at or below the stacked breakpoint. */
const WINDOW_WIDTH = 360
/** styles.css: below ~1000px the three workspace panels stack vertically. */
export const STACKED_BREAKPOINT = 1000

/**
 * Lifecycle events (consumed by guided tours part 3). `completed` means every step's condition was
 * met in order; a tour that reached its end with a skipped step reports `abandoned` at its last step
 * instead. `step` fires once per step the first time the run reaches it: revisiting steps with
 * Back / Next emits nothing.
 */
export interface TourEvent {
  type: 'started' | 'step' | 'completed' | 'abandoned'
  tourId: string
  version: number
  /** 0-based step index. */
  step: number
  lang: Lang
}

interface Running {
  def: TourDef
  step: number
  /** The furthest step reached in this run: Next never goes past it unless the step is done. */
  furthest: number
  startedAt: number
  skipped: boolean
}

interface TourApi {
  /** The running tour and its 0-based step, or null. */
  active: { tourId: string; step: number } | null
  /** Starts a tour from its first step (navigates to its `startView`, when it has one). */
  start: (tourId: string) => void
  /** Stops the running tour (reported as abandoned). */
  stop: () => void
}

const TourContext = createContext<TourApi>({ active: null, start: () => {}, stop: () => {} })

/** Starts/stops tours from anywhere under `TourProvider`. */
export function useTour(): TourApi {
  return useContext(TourContext)
}

/** Fills `{name}` placeholders of a `tour_*` string. */
function fill(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m))
}

/**
 * Where `startView` is, from the current route — or null to stay put. A bid-scoped view needs the
 * bid already open: bid ids are per user, so a tour can never name one.
 */
function pathForView(view: View, pathname: string): string | null {
  const { bid } = parseRoute(pathname)
  switch (view) {
    case 'home': return homePath
    case 'releases': return releasesPath
    case 'account-settings': return accountSettingsPath
    case 'corporate-templates': return corporateTemplatesPath
    case 'version-retention': return versionRetentionPath
    case 'feedback-triage': return feedbackTriagePath
    case 'editor': return bid ? bidPath(bid) : null
    case 'knowledge': return bid ? knowledgePath(bid) : null
    case 'dashboard': return bid ? dashboardPath(bid) : null
    case 'settings': return bid ? settingsPath(bid) : null
    // A template view needs a template id, which no tour can name (they are per bid).
    case 'template': return null
  }
}

/** The pending tour from `sessionStorage`, if it still matches a tour of this build. */
function loadResumable(tours: readonly TourDef[]): { saved: ActiveTour; def: TourDef } | null {
  const saved = readActiveTour()
  if (!saved) return null
  const def = tours.find((t) => t.id === saved.id)
  if (!def || def.version !== saved.version || saved.step < 0 || saved.furthest >= def.steps.length) {
    // The tour was removed or its steps changed since: the saved position means nothing.
    clearActiveTour()
    return null
  }
  return { saved, def }
}

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
/** Focus is never moved away from these: a modal dialog, the prose editor, the slides canvas. */
const KEEP_FOCUS = '[role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"], .ProseMirror, .sl-stage'

function focusTargetOf(anchor: HTMLElement): HTMLElement | null {
  if (anchor.matches(FOCUSABLE)) return anchor
  return anchor.querySelector<HTMLElement>(FOCUSABLE)
}

const subscribeFocusMode = (cb: () => void) => subscribeTourState(cb)
const focusModeOn = () => !!document.querySelector('.app.focus')

/**
 * The user's 👍 / 👎 on a tour, for the congratulations card (App.tsx wires it to userhub). The
 * engine knows nothing of where verdicts are kept.
 */
export interface TourVerdicts {
  /** true 👍, false 👎, null no opinion yet; undefined: a verdict cannot be offered now (the buttons hide). */
  verdictOf: (number: number) => boolean | null | undefined
  setVerdict: (number: number, liked: boolean | null) => void
}

interface TourProviderProps {
  children?: ReactNode
  /** The tours this provider can run. Tests pass a fake tour. */
  tours?: readonly TourDef[]
  onEvent?: (e: TourEvent) => void
  /** 👍 / 👎 on the congratulations card; none → no buttons. */
  verdicts?: TourVerdicts
  /** Test hook; production uses {@link ANCHOR_TIMEOUT_MS}. */
  anchorTimeoutMs?: number
}

/**
 * The tour engine, mounted once in App.tsx around every shell, so a tour can lead from the home
 * page into a bid. It rings each step's anchor and advances on **Next** or when the step's probe
 * becomes true. **Back** revisits earlier steps; **Next** then replays up to the furthest step
 * reached, never beyond an unfinished one. It only ever navigates to a tour's `startView`; it never
 * clicks, types or writes on the user's behalf.
 */
export function TourProvider(props: TourProviderProps) {
  return (
    <RatingProvider>
      <TourEngine {...props} />
    </RatingProvider>
  )
}

function TourEngine({ children, tours = TOUR_DEFS, onEvent, verdicts, anchorTimeoutMs = ANCHOR_TIMEOUT_MS }: TourProviderProps) {
  const { t, lang } = useLang()
  const pathname = usePath()
  const [running, setRunning] = useState<Running | null>(null)
  const [resumable, setResumable] = useState(() => loadResumable(tours))
  // The tour just completed, while its congratulations card is shown. Not saved in the session:
  // a reload, a resume or a restart never shows it again.
  const [congrats, setCongrats] = useState<TourDef | null>(null)
  const [announcement, setAnnouncement] = useState('')

  const emitRef = useRef<(type: TourEvent['type'], r: Running, step?: number) => void>(() => {})
  emitRef.current = (type, r, step = r.step) =>
    onEvent?.({ type, tourId: r.def.id, version: r.def.version, step, lang })

  // The current tour + step survive a reload of this tab.
  useEffect(() => {
    if (running) {
      writeActiveTour({
        id: running.def.id, version: running.def.version, step: running.step,
        furthest: running.furthest, startedAt: running.startedAt,
      })
    } else {
      setAnnouncement('')
    }
  }, [running])

  // Read through a ref so start/stop/advance emit each event exactly once (no side
  // effects inside a state updater, which StrictMode runs twice).
  const runningRef = useRef(running)
  runningRef.current = running
  const pathRef = useRef(pathname)
  pathRef.current = pathname

  const start = useCallback((tourId: string) => {
    const def = tours.find((d) => d.id === tourId)
    if (!def || def.steps.length === 0) return
    if (runningRef.current) emitRef.current('abandoned', runningRef.current)
    const r: Running = { def, step: 0, furthest: 0, startedAt: Date.now(), skipped: false }
    runningRef.current = r
    setResumable(null)
    setCongrats(null)
    setRunning(r)
    emitRef.current('started', r)
    if (def.startView && parseRoute(pathRef.current).view !== def.startView) {
      const to = pathForView(def.startView, pathRef.current)
      if (to) navigate(to)
    }
  }, [tours])

  const stop = useCallback(() => {
    const r = runningRef.current
    if (r) emitRef.current('abandoned', r)
    runningRef.current = null
    setRunning(null)
    clearActiveTour()
  }, [])

  const advance = useCallback((skipped: boolean) => {
    const r = runningRef.current
    if (!r) return
    const next = r.step + 1
    const wasSkipped = r.skipped || skipped
    if (next >= r.def.steps.length) {
      emitRef.current(wasSkipped ? 'abandoned' : 'completed', r)
      runningRef.current = null
      setRunning(null)
      clearActiveTour()
      if (!wasSkipped) setCongrats(r.def)
      return
    }
    const n = { ...r, step: next, furthest: Math.max(r.furthest, next), skipped: wasSkipped }
    runningRef.current = n
    setRunning(n)
    if (next > r.furthest) emitRef.current('step', n)
  }, [])

  // Back never undoes anything the user did: it only shows the previous step again.
  const back = useCallback(() => {
    const r = runningRef.current
    if (!r || r.step === 0) return
    const n = { ...r, step: r.step - 1 }
    runningRef.current = n
    setRunning(n)
  }, [])

  // Resume: the same step, no navigation.
  const resume = useCallback(() => {
    if (!resumable) return
    const { saved, def } = resumable
    setResumable(null)
    setRunning({ def, step: saved.step, furthest: saved.furthest, startedAt: saved.startedAt, skipped: false })
  }, [resumable])

  const dismissResume = useCallback(() => {
    if (!resumable) return
    const { saved, def } = resumable
    onEvent?.({ type: 'abandoned', tourId: def.id, version: def.version, step: saved.step, lang })
    setResumable(null)
    clearActiveTour()
  }, [resumable, onEvent, lang])

  const api = useMemo<TourApi>(() => ({
    active: running ? { tourId: running.def.id, step: running.step } : null,
    start,
    stop,
  }), [running, start, stop])

  const resumeTitleId = useId()

  return (
    <TourContext.Provider value={api}>
      {children}
      {/* The step is read out at each step, wherever focus is. One region for the whole run, so
          each new step is a change to an existing live region (a fresh one may not be read). */}
      <div role="status" aria-live="polite" data-testid="tour-announcer" className="visually-hidden">{announcement}</div>
      {running && (
        <TourStepView
          key={`${running.def.id}:${running.step}`}
          def={running.def}
          index={running.step}
          furthest={running.furthest}
          startedAt={running.startedAt}
          pathname={pathname}
          anchorTimeoutMs={anchorTimeoutMs}
          onAnnounce={setAnnouncement}
          onNext={() => advance(false)}
          onBack={back}
          onSkip={() => advance(true)}
          onStop={stop}
        />
      )}
      {congrats && !running && (
        <CongratsView key={congrats.id} def={congrats} verdicts={verdicts} onClose={() => setCongrats(null)} />
      )}
      {!running && resumable && createPortal(
        <div
          className="tour-card tour-resume"
          role="dialog"
          aria-labelledby={resumeTitleId}
          data-testid="tour-resume-toast"
          data-tour-window=""
        >
          <p id={resumeTitleId} className="tour-card-text">
            {t('tour_resume_prompt')} <strong>{resumable.def.title[lang]}</strong>
          </p>
          <div className="tour-actions">
            <button type="button" className="btn" onClick={dismissResume} data-testid="tour-resume-stop">{t('tour_stop')}</button>
            <button type="button" className="btn primary" onClick={resume} data-testid="tour-resume">{t('tour_resume')}</button>
          </div>
        </div>,
        document.body,
      )}
    </TourContext.Provider>
  )
}

// ── One step ────────────────────────────────────────────────────────────────────

interface TourStepViewProps {
  def: TourDef
  index: number
  furthest: number
  startedAt: number
  pathname: string
  anchorTimeoutMs: number
  onAnnounce: (text: string) => void
  onNext: () => void
  onBack: () => void
  onSkip: () => void
  onStop: () => void
}

/** Finds the step's anchor and keeps tracking it: it may mount late or re-mount. */
function useAnchor(step: TourStep, timeoutMs: number): { anchor: HTMLElement | null; missing: boolean } {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [timedOut, setTimedOut] = useState(false)

  useEffect(() => {
    const selector = helpIdSelector(step.target)
    const find = () => {
      const el = document.querySelector<HTMLElement>(selector)
      setAnchor((prev) => (prev === el ? prev : el))
    }
    find()
    const mo = new MutationObserver(find)
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-help-id'] })
    return () => mo.disconnect()
  }, [step.target])

  // The 5 s clock runs while the anchor is absent, and restarts if it disappears again.
  useEffect(() => {
    if (anchor) { setTimedOut(false); return }
    const timer = setTimeout(() => setTimedOut(true), timeoutMs)
    return () => clearTimeout(timer)
  }, [anchor, timeoutMs])

  return { anchor, missing: !anchor && timedOut }
}

function TourStepView({
  def, index, furthest, startedAt, pathname, anchorTimeoutMs, onAnnounce, onNext, onBack, onSkip, onStop,
}: TourStepViewProps) {
  const { t, lang } = useLang()
  const step = def.steps[index]!
  const { anchor, missing } = useAnchor(step, anchorTimeoutMs)
  const rect = useAnchorRect(anchor)
  const cond = step.advanceWhen === 'next' ? null : step.advanceWhen
  const uid = useId()
  const titleId = `${uid}-title`
  const bodyId = `${uid}-body`
  const blockedId = `${uid}-blocked`
  const inFocusMode = useSyncExternalStore(subscribeFocusMode, focusModeOn)

  // A probe step advances when its condition *becomes* true. When it already holds as the step
  // opens, the step offers Next instead of flashing past.
  const [metAtStart] = useState(() => !!cond && evaluateCondition(cond, makeProbeContext(pathname, startedAt)))
  const [met, setMet] = useState(metAtStart)
  const [empty, setEmpty] = useState(false)
  const metRef = useRef(metAtStart)
  const pathRef = useRef(pathname)
  pathRef.current = pathname
  const onNextRef = useRef(onNext)
  onNextRef.current = onNext

  // A step advances once. Its state may flicker true → false → true before the next step
  // renders (StrictMode re-runs a screen's publish/clear effects): that is not a second action.
  const advancedRef = useRef(false)

  const check = useCallback(() => {
    if (!cond || advancedRef.current) return
    const ctx = makeProbeContext(pathRef.current, startedAt)
    const now = evaluateCondition(cond, ctx)
    setEmpty(evaluateEmpty(cond, ctx))
    setMet(now)
    if (now && !metRef.current) {
      metRef.current = true
      advancedRef.current = true
      onNextRef.current()
      return
    }
    metRef.current = now
  }, [cond, startedAt])

  useEffect(() => subscribeTourState(check), [check])
  useEffect(() => { check() }, [check, pathname])

  // Focus goes to the anchor once per step (not again if the screen re-mounts it while the user
  // works); Tab from it reaches the step window. Never while focus is in a modal dialog, the prose
  // editor or the slides canvas: nobody loses their caret to a tour.
  const surfaceRef = useRef<HTMLDivElement | null>(null)
  const focusedRef = useRef(false)
  useEffect(() => {
    if (!anchor) return
    const target = focusTargetOf(anchor)
    if (!focusedRef.current) {
      focusedRef.current = true
      if (!document.activeElement?.closest(KEEP_FOCUS)) {
        anchor.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
        target?.focus({ preventScroll: true })
      }
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || e.shiftKey || !target || e.target !== target) return
      const first = surfaceRef.current?.querySelector<HTMLElement>('button:not([disabled])')
      if (first) { e.preventDefault(); first.focus() }
    }
    anchor.addEventListener('keydown', onKeyDown)
    return () => anchor.removeEventListener('keydown', onKeyDown)
  }, [anchor])

  // The step window sits next to the anchor and follows it, never covering the anchor, the open
  // popups, nor what the step lists in `avoid`. The user may drag it by its title bar; it then stays
  // where they put it for this step only — unless a popup opened since would be under it: it is
  // then nudged out of the way. The next step (a new TourStepView, keyed by step) re-anchors.
  const avoidSet = useAvoidSet(anchor, step.avoid)
  const [moved, setMoved] = useState<Point | null>(null)
  const [dragging, setDragging] = useState(false)
  // The popups already under the window when the user dropped it there: their choice, left alone.
  const [droppedOver, setDroppedOver] = useState<ReadonlySet<Element>>(() => new Set())
  const titleRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  // The window's natural size, unshrunk: the title bar + the whole body + the borders.
  const [size, setSize] = useState({ w: WINDOW_WIDTH, h: 0 })
  useLayoutEffect(() => {
    const el = surfaceRef.current
    if (!el) return
    const measure = () => setSize((prev) => {
      const w = el.offsetWidth || WINDOW_WIDTH
      const h = (titleRef.current?.offsetHeight ?? 0) + (contentRef.current?.offsetHeight ?? 0) + 2 * RING_WIDTH
      return prev.w === w && prev.h === h ? prev : { w, h }
    })
    measure()
    if (typeof ResizeObserver !== 'function') return
    const ro = new ResizeObserver(measure)
    ;[el, titleRef.current, contentRef.current].forEach((n) => n && ro.observe(n))
    return () => ro.disconnect()
  }, [anchor])

  const vp = avoidSet.viewport.width ? avoidSet.viewport : { width: window.innerWidth, height: window.innerHeight }
  // Below the stacked breakpoint the window docks full-width at the bottom (CSS), and is not dragged.
  const docked = vp.width <= STACKED_BREAKPOINT
  const auto = rect ? computePlacement({
    target: rect, avoid: avoidSet.items.map((i) => i.rect), size, viewport: vp, placement: step.placement,
  }) : null
  const newPopups = avoidSet.items.filter((i) => i.kind === 'popup' && !droppedOver.has(i.el)).map((i) => i.rect)
  const nudged = moved && !dragging ? nudgeOut(moved, size, newPopups, vp) : moved
  const pos: Point | null = moved ? (nudged ?? auto) : auto
  const maxHeight = !moved && auto?.maxHeight != null ? auto.maxHeight : undefined

  const dragRef = useRef<{ px: number; py: number; x: number; y: number } | null>(null)
  const onDragStart = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (docked || !pos || e.button !== 0) return
    e.currentTarget.setPointerCapture?.(e.pointerId)
    dragRef.current = { px: e.clientX, py: e.clientY, x: pos.x, y: pos.y }
    setDragging(true)
  }
  const onDragMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const o = dragRef.current
    if (!o) return
    setMoved(clampToViewport({ x: o.x + e.clientX - o.px, y: o.y + e.clientY - o.py }, size, vp))
  }
  const onDragEnd = () => {
    if (!dragRef.current) return
    dragRef.current = null
    setDragging(false)
    const at = surfaceRef.current?.getBoundingClientRect()
    setDroppedOver(new Set(at ? avoidSet.items.filter((i) => i.kind === 'popup'
      && i.rect.left < at.right && at.left < i.rect.left + i.rect.width
      && i.rect.top < at.bottom && at.top < i.rect.top + i.rect.height).map((i) => i.el) : []))
  }

  const title = step.title[lang]
  const body = step.body[lang]
  const stepOf = fill(t('tour_step_of'), { n: index + 1, total: def.steps.length })
  const isLast = index === def.steps.length - 1
  // Next replays the steps already reached; past the furthest one, only a step that is done
  // (Next-only, or its condition holds now) lets the user go on — never skip ahead.
  const canNext = index < furthest || !cond || met
  const hint = empty && step.emptyHint ? step.emptyHint[lang] : null
  const missingText = inFocusMode ? `${t('tour_anchor_missing')}. ${t('tour_leave_focus')}.` : t('tour_anchor_missing')

  const announce = missing
    ? `${stepOf}. ${title}. ${missingText}`
    : anchor ? `${stepOf}. ${title}. ${tourMarkdownToPlain(body)}${hint ? ` ${hint}` : ''}` : ''
  useEffect(() => { if (announce) onAnnounce(announce) }, [announce, onAnnounce])

  // Esc inside a tour window stops the tour, and only the tour (App's focus-mode Esc skips it).
  const onEscape = (e: ReactKeyboardEvent) => {
    if (e.key !== 'Escape') return
    e.preventDefault()
    e.stopPropagation()
    onStop()
  }

  return (
    <>
      {anchor && <HighlightRing anchor={anchor} />}

      {anchor && createPortal(
        // Not modal: the user must stay free to act on the anchor (and the app) with the mouse, the
        // keyboard and a screen reader. An outside click never closes the step.
        <div
          ref={surfaceRef}
          className={'tour-window' + (docked ? ' docked' : '') + (dragging ? ' dragging' : '')}
          role="dialog"
          aria-labelledby={titleId}
          aria-describedby={bodyId}
          data-testid="tour-popover"
          data-tour-window=""
          onKeyDown={onEscape}
          style={docked ? undefined : {
            left: pos?.x ?? 0, top: pos?.y ?? 0, visibility: pos ? 'visible' : 'hidden',
            ...(maxHeight != null ? { maxHeight } : {}),
          }}
        >
          <div
            ref={titleRef}
            className="tour-titlebar"
            data-testid="tour-titlebar"
            title={docked ? undefined : t('tour_move_hint')}
            onPointerDown={onDragStart}
            onPointerMove={onDragMove}
            onPointerUp={onDragEnd}
            onPointerCancel={onDragEnd}
          >
            <span className="tour-step-of" data-testid="tour-step-of">{stepOf}</span>
            <span className="tour-title" id={titleId}>{title}</span>
          </div>
          {/* Scrolls when the window had to shrink to stay clear of the avoid set. */}
          <div className="tour-scroll" data-testid="tour-step-scroll">
            <div ref={contentRef} className="tour-content">
              <div id={bodyId} className="tour-body"><TourMarkdown text={body} /></div>
              {hint && <p className="tour-hint" data-testid="tour-empty-hint">{hint}</p>}
              {!canNext && <p className="tour-blocked" id={blockedId}>{t('tour_next_blocked')}</p>}
              <div className="tour-actions">
                <button type="button" className="btn tour-stop" onClick={onStop} data-testid="tour-stop">{t('tour_stop')}</button>
                <span className="tour-spacer" />
                <button type="button" className="btn" disabled={index === 0} onClick={onBack} data-testid="tour-back">
                  {t('tour_back')}
                </button>
                {/* Focusable while blocked, so a keyboard / screen-reader user still finds it and hears why. */}
                <button
                  type="button"
                  className="btn primary"
                  aria-disabled={canNext ? undefined : true}
                  aria-describedby={canNext ? undefined : blockedId}
                  onClick={canNext ? onNext : undefined}
                  data-testid="tour-next"
                >
                  {isLast ? t('tour_finish') : t('tour_next')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {missing && createPortal(
        <div
          className="tour-card tour-missing"
          role="dialog"
          aria-labelledby={`${uid}-missing`}
          data-testid="tour-anchor-missing"
          data-tour-window=""
          onKeyDown={onEscape}
        >
          <span className="tour-step-of">{stepOf}</span>
          <p className="tour-title" id={`${uid}-missing`}>{title}</p>
          <p className="tour-card-text">{t('tour_anchor_missing')}</p>
          {inFocusMode && <p className="tour-card-text" data-testid="tour-leave-focus">{t('tour_leave_focus')}</p>}
          <div className="tour-actions">
            <button type="button" className="btn" onClick={onStop} data-testid="tour-stop">{t('tour_stop')}</button>
            <button type="button" className="btn primary" onClick={onSkip} data-testid="tour-skip">{t('tour_skip_step')}</button>
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}

// ── The congratulations card ────────────────────────────────────────────────────

/**
 * Shown once, right after a tour is completed: not a tour step (no "Step n of N", no Back /
 * Next). Glitter falls behind it (none under reduced motion). "Rate the app" opens the userhub
 * widget's own Rate dialog, and only shows when the widget offers one. 👍 / 👎 (toggles) give the
 * user's verdict on the tour; they hide while it cannot be recorded (`verdicts.verdictOf` undefined).
 */
function CongratsView({ def, verdicts, onClose }: { def: TourDef; verdicts?: TourVerdicts; onClose: () => void }) {
  const { t, lang } = useLang()
  const openRating = useOpenRating()
  const closeRef = useRef<HTMLButtonElement | null>(null)
  const uid = useId()
  useEffect(() => { closeRef.current?.focus({ preventScroll: true }) }, [])
  const text = fill(t('tour_congrats_text'), { tour: def.title[lang] })
  const verdict = verdicts?.verdictOf(def.number)
  const toggle = (liked: boolean) => verdicts?.setVerdict(def.number, verdict === liked ? null : liked)

  return (
    <>
      <Confetti />
      {createPortal(
        <div
          className="tour-window tour-congrats"
          role="dialog"
          aria-labelledby={`${uid}-title`}
          aria-describedby={`${uid}-body`}
          data-testid="tour-congrats"
          data-tour-window=""
          onKeyDown={(e) => {
            if (e.key !== 'Escape') return
            e.preventDefault()
            e.stopPropagation()
            onClose()
          }}
        >
          <div className="tour-titlebar static">
            <span className="tour-title tour-congrats-title" id={`${uid}-title`}>{t('tour_congrats_title')}</span>
          </div>
          <div className="tour-content">
            <p className="tour-card-text" id={`${uid}-body`}>{text}</p>
            <div className="tour-actions end">
              {verdict !== undefined && (
                <>
                  <button
                    type="button"
                    className="btn tour-verdict"
                    aria-label={t('tour_like')}
                    title={t('tour_like')}
                    aria-pressed={verdict === true}
                    data-testid="tour-congrats-like"
                    onClick={() => toggle(true)}
                  >
                    <span aria-hidden="true">&#128077;</span>
                  </button>
                  <button
                    type="button"
                    className="btn tour-verdict"
                    aria-label={t('tour_dislike')}
                    title={t('tour_dislike')}
                    aria-pressed={verdict === false}
                    data-testid="tour-congrats-dislike"
                    onClick={() => toggle(false)}
                  >
                    <span aria-hidden="true">&#128078;</span>
                  </button>
                  <span className="tour-spacer" />
                </>
              )}
              {openRating && (
                <button
                  type="button"
                  className="btn primary"
                  data-testid="tour-congrats-rate"
                  onClick={() => { onClose(); openRating() }}
                >
                  {t('tour_rate')}
                </button>
              )}
              <button type="button" className="btn" ref={closeRef} onClick={onClose} data-testid="tour-congrats-close">
                {t('tour_close')}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}

/** Whether `el` is part of a tour window (App's global keys leave those alone). */
export function inTourWindow(el: Element | null): boolean {
  return !!el?.closest(TOUR_WINDOW)
}
